import React, { useLayoutEffect, useState } from 'react'
import { Link } from 'gatsby'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import PropTypes from 'prop-types'
import { Popover } from '@mui/material'
import { navItems } from './navbar.static'
import './navbar.css'
import MenuDrawer from './menuDrawer'
import MenuSection from './menuSection'
import LangSwitcher from 'components/langSwitcher/popover'
import Icon from 'components/icon'

import { getPortalHost } from 'utils'

const MonetizationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={28} height={28} fill="none" className="mr-2">
    <mask
      id="a"
      width={28}
      height={28}
      x={0}
      y={0}
      maskUnits="userSpaceOnUse"
      style={{
        maskType: 'alpha'
      }}
    >
      <path fill="#D9D9D9" d="M0 0h28v28H0z" />
    </mask>
    <g mask="url(#a)">
      <path
        fill="#3A3A3A"
        d="M14 26.833c-2.178 0-4.18-.495-6.008-1.487-1.828-.992-3.325-2.314-4.492-3.967V24.5H1.167v-7h7v2.333H5.278c.933 1.4 2.163 2.528 3.69 3.384 1.526.855 3.203 1.283 5.03 1.283 1.46 0 2.825-.277 4.099-.831a10.671 10.671 0 0 0 3.325-2.246 10.675 10.675 0 0 0 2.246-3.325c.554-1.274.83-2.64.83-4.098h2.334c0 1.77-.335 3.432-1.006 4.988a12.99 12.99 0 0 1-2.756 4.083 12.99 12.99 0 0 1-4.084 2.756c-1.555.671-3.218 1.006-4.987 1.006Zm-1.05-4.666V20.65c-.914-.214-1.658-.608-2.231-1.181-.574-.574-.997-1.327-1.27-2.26l1.926-.759c.233.797.598 1.395 1.094 1.794a2.647 2.647 0 0 0 1.706.598c.641 0 1.19-.15 1.648-.452.457-.302.685-.773.685-1.415 0-.564-.238-1.02-.714-1.37-.477-.35-1.328-.75-2.553-1.197-1.147-.408-1.988-.894-2.522-1.458-.535-.564-.803-1.303-.803-2.217 0-.797.278-1.521.832-2.173.554-.65 1.307-1.074 2.26-1.268V5.833h2.042v1.459c.7.058 1.337.34 1.91.846.574.505.968 1.098 1.181 1.779l-1.866.758a2.566 2.566 0 0 0-.758-1.123c-.35-.301-.837-.452-1.459-.452-.68 0-1.2.146-1.56.438-.36.291-.54.69-.54 1.195 0 .506.224.905.671 1.196.447.292 1.254.632 2.42 1.021 1.4.506 2.334 1.099 2.8 1.78.468.68.7 1.428.7 2.245 0 .564-.096 1.06-.29 1.488a3.427 3.427 0 0 1-.774 1.093c-.32.302-.695.545-1.123.73a6.82 6.82 0 0 1-1.37.422v1.459H12.95ZM1.166 14c0-1.77.336-3.432 1.007-4.987a12.989 12.989 0 0 1 2.756-4.084 12.988 12.988 0 0 1 4.083-2.756c1.556-.67 3.218-1.006 4.988-1.006 2.178 0 4.18.496 6.008 1.487 1.828.992 3.325 2.314 4.492 3.967V3.5h2.333v7h-7V8.166h2.888c-.934-1.4-2.163-2.528-3.69-3.384C17.505 3.928 15.828 3.5 14 3.5c-1.459 0-2.825.277-4.098.831a10.673 10.673 0 0 0-3.325 2.246A10.673 10.673 0 0 0 4.33 9.902 10.158 10.158 0 0 0 3.5 14H1.167Z"
      />
    </g>
  </svg>
)

const Navbar = ({ className = '' }) => {
  const { t } = useTranslation()
  const [portalHost, setPortalHost] = useState('')
  const [targetNavItem, setTargetNavItem] = useState({ el: null, id: null })
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const handlePopoverClose = () => {
    setTargetNavItem({ el: null, id: null })
  }
  const handlePopoverOpen = (event, itemName) => {
    setTargetNavItem({ el: event?.currentTarget || targetNavItem.el, id: itemName })
  }
  const isOpenNavItem = (itemName) => targetNavItem.id === itemName
  const toggleDrawer = (open) => () => {
    setIsDrawerOpen(open)
  }

  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      setPortalHost(getPortalHost())
    }
  })

  return (
    <header className={`bg-white w-full z-20 ${className}`}>
      <nav className="flex flex-row items-center justify-between px-9 py-4" style={{ fontFamily: 'Noto Sans' }}>
        <Link to="/" className="shrink-0 text-xl font-medium">
          <img
            alt="Mlytics"
            src="https://www.mlytics.com/wp-content/uploads/2021/11/logo_Mlytics_regular.svg"
            height="75"
            width="150"
          />
        </Link>
        <button
          onClick={toggleDrawer(true)}
          className="flex items-center justify-center xl:hidden h-10 leading-10 w-10"
        >
          <Icon className="text-primary-500" name="menu_outlined" />
        </button>
        <MenuDrawer isOpen={isDrawerOpen} closeMenu={toggleDrawer(false)} />
        <div className="hidden xl:flex flex-row items-center justify-end">
          {navItems.map((navItem) => (
            <div key={navItem.name}>
              <a
                aria-owns={isOpenNavItem(navItem.name) ? navItem.name : undefined}
                aria-haspopup="true"
                onMouseEnter={(e) => handlePopoverOpen(e, navItem.name)}
                onMouseLeave={handlePopoverClose}
                className="flex font-bold items-center leading-9 px-4 text-gray-600 text-sm uppercase duration-200 ease-linear hover:text-secondary-600"
                href={navItem.link || '#'}
              >
                {t(navItem.name)}
                {navItem.list ? <Icon fontSize="16px" name="arrow_drop_down_outlined" /> : null}
              </a>
              {navItem.list ? (
                <Popover
                  anchorEl={targetNavItem?.el}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                  }}
                  classes={{ paper: `px-6 py-5 rounded-md shadow-md pointer-events-auto` }}
                  className="pointer-events-none"
                  disableScrollLock={true}
                  id={navItem.name}
                  open={isOpenNavItem(navItem.name)}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                  }}
                  PaperProps={{
                    onMouseEnter: () => handlePopoverOpen(null, navItem.name),
                    onMouseLeave: handlePopoverClose
                  }}
                >
                  {navItem.key === 'products' ? (
                    <section
                      className="table-like grid gap-3"
                      style={{ gridTemplateColumns: 'minmax(80px, 155px) repeat(4, minmax(155px, 210px))' }}
                    >
                      {/* <React.Fragment> */}
                      {/* //table header rows */}
                      {/* <div></div> */}
                      {/* {navItem.list[0]?.list.map((thItem) => (
                          <div className="th flex items-center" key={thItem.name}>
                            <img
                              className="mr-1"
                              src={`https://www.mlytics.com/wp-content/uploads/2023/05/icon-${thItem.key}.svg`}
                            />
                            <span>{t(thItem.name)}</span>
                          </div>
                        ))} */}
                      {/* </React.Fragment> */}
                      {navItem.list.map((row) => (
                        <React.Fragment key={row.name}>
                          {/* table body rows */}
                          <p
                            className="flex items-center justify-center rounded-xl text-center h-full"
                            style={{ height: 185 }}
                          >
                            <span className="font-black text-lg">{t(row.name)}</span>
                          </p>
                          {row.list.map((rowItem) => (
                            <div key={rowItem.name}>
                              <div className="th mb-2 flex items-center" key={rowItem.name}>
                                {rowItem.key === 'monetization' ? (
                                  <MonetizationIcon />
                                ) : (
                                  <img
                                    className="mr-1 "
                                    src={`https://www.mlytics.com/wp-content/uploads/2023/05/icon-${rowItem.key}.svg`}
                                  />
                                )}
                                <span>{t(rowItem.name)}</span>
                              </div>
                              {rowItem.list.map((menuItem) => {
                                return menuItem.disable ? (
                                  <div
                                    key={menuItem.name}
                                    className="cursor-not-allowed flex items-center text-gray-700/25"
                                  >
                                    {/* <Icon name="architecture_outlined" /> */}
                                    <span className="leading-normal text-sm">{t(menuItem.name)}</span>
                                  </div>
                                ) : (
                                  <a key={menuItem.name} href={menuItem.link} className="px-2 mb-2">
                                    {t(menuItem.name)}
                                  </a>
                                )
                              })}
                            </div>
                          ))}
                        </React.Fragment>
                      ))}
                    </section>
                  ) : (
                    <section>
                      {navItem?.list?.map((listItem) => (
                        <MenuSection key={listItem.name} sectionItem={listItem} />
                      ))}
                    </section>
                  )}
                </Popover>
              ) : null}
            </div>
          ))}
          <LangSwitcher className="flex h-9 items-center pl-10 pr-3" />
          <a
            className="border-l border-blue-300 font-semibold px-5 text-gray-600 text-sm uppercase duration-200 ease-linear hover:text-secondary-600"
            href={`${portalHost}/login`}
          >
            {t('navigation.buttonText')}
          </a>
          <Link
            to="/signup/"
            className="btn bg-blue-300 border-2 border-blue-300 font-semibold text-sm text-white uppercase"
          >
            {t('button.freeTrial.2')}
          </Link>
          <Link
            to="/lp/get-a-demo/"
            className="btn border-2 border-blue-300 font-semibold text-blue-300 text-sm uppercase ml-3"
          >
            {t('button.getDemo.0')}
          </Link>
        </div>
      </nav>
    </header>
  )
}
Navbar.propTypes = {
  className: PropTypes.string
}

export default Navbar
